<template>
  <v-layout row wrap v-if="getImport">
    <v-flex xs12 v-if="getImport.status === 'preview-ready'">
      <h3>Queueing Validation</h3>
      <v-progress-linear indeterminate />
    </v-flex>
    <v-flex xs12 v-else-if="getImport.status === 'validating'">
      <h3>Validating</h3>
      <v-progress-linear
        :indeterminate="getImport.progress == null"
        :value="getImport.progress"
        height="25"
        class="tw-mb-1"
      >
      </v-progress-linear>
      <strong class="tw-text-center tw-w-full tw-block">{{
        getImport.progress == null ? 'Initializing...' : getImport.progress + '%'
      }}</strong>
    </v-flex>
    <v-flex xs12 v-else-if="getImport.status === 'ready-to-import'">
      <h3>
        <v-icon color="success">check</v-icon>
        Validation Succeeded
      </h3>
      <v-btn @click="beginImport">Run Import</v-btn>
    </v-flex>
    <v-flex xs12 v-else-if="getImport.status === 'validation-failed'">
      <h3>
        <v-icon color="warning">warning</v-icon>
        Validation Failed
      </h3>
    </v-flex>
    <v-flex xs12 v-else-if="getImport.status === 'running'">
      <h3>Running Import</h3>
      <v-progress-linear
        :indeterminate="getImport.progress == null"
        :value="getImport.progress"
        height="25"
        class="tw-mb-1"
      >
      </v-progress-linear>
      <strong class="tw-text-center tw-w-full tw-block">{{
        getImport.progress == null ? 'Processing...' : getImport.progress + '%'
      }}</strong>
    </v-flex>
    <v-flex xs12 v-else-if="getImport.status === 'completed' || getImport.status === 'completed-with-warnings'">
      <h3>
        <v-icon color="success">check</v-icon>
        Import Complete
      </h3>
    </v-flex>
    <v-flex xs12 v-else-if="getImport.status === 'completed-with-errors'">
      <h3>
        <v-icon color="warning">warning</v-icon>
        Import Completed With Errors
      </h3>
    </v-flex>
    <v-flex xs12 v-else-if="getImport.status === 'cancelled'">
      <h3>
        <v-icon color="warning">cancel</v-icon>
        Import Cancelled
      </h3>
    </v-flex>
    <v-flex xs12 v-else-if="getImport.status === 'failed'">
      <h3>
        <v-icon color="error">error</v-icon>
        Import Failed
      </h3>
    </v-flex>
    <v-flex xs12 v-else> Unknown state: {{ getImport }} </v-flex>

    <!-- Surface ALL errors regardless of state. The legacy page only read the old
         validationErrors field, so v2 imports (which write to rowErrors/importErrors)
         showed an empty table on "Validation Failed". -->
    <v-flex xs12 v-if="showErrors" class="tw-mt-4">
      <h4>
        <v-icon color="warning" small>warning</v-icon>
        Errors<span v-if="rowErrorCount && (rowErrorCount.validate || rowErrorCount.run)">
          ({{ rowErrorCount.validate || 0 }} validation, {{ rowErrorCount.run || 0 }} import)</span
        >
      </h4>
      <p v-if="moreErrorsThanShown" class="tw-text-sm">
        Showing {{ shownRowCount }}<span v-if="rowErrorCount"> of {{ totalRowCount }}</span> error rows — fix these and
        re-import to see the rest.
      </p>
      <table v-if="errorRows.length > 0" style="width: 100%">
        <tr>
          <th>Line</th>
          <th>Stage</th>
          <th>Errors</th>
        </tr>
        <tr v-for="(row, index) in errorRows" v-bind:key="`err-${index}`">
          <td>{{ row.line }}</td>
          <td>{{ row.stage }}</td>
          <td>
            <ul>
              <li v-for="(e, ei) in row.errors" v-bind:key="`err-${index}-${ei}`">{{ e }}</li>
            </ul>
          </td>
        </tr>
      </table>
      <ul v-if="errorMessages.length > 0">
        <li v-for="(m, mi) in errorMessages" v-bind:key="`msg-${mi}`">{{ m }}</li>
      </ul>
      <p v-if="errorDetailsUnavailable" class="tw-text-sm">
        Couldn't load the specific errors for this import. Please retry, and contact support if this keeps happening.
      </p>
    </v-flex>
  </v-layout>
</template>

<script>
import gql from 'graphql-tag';

// Terminal states that carry errors worth showing. Gates BOTH the error-details
// fetch and the display (showErrors), so they stay in lockstep and stale details
// can't render under a non-error status. Deliberately excludes
// completed-with-warnings (rendered as success) and cancelled (user-aborted, not
// an error) — neither should surface an "Errors" table.
const ERROR_DETAIL_STATES = ['validation-failed', 'completed-with-errors', 'failed'];

// States the import can no longer transition out of on its own — once here, the
// status poll can stop (interactive states like validation-failed/ready-to-import
// keep polling to catch the next user-driven move).
const TERMINAL_STATES = ['completed', 'completed-with-warnings', 'completed-with-errors', 'cancelled', 'failed'];

// Per-stage cap on how many row errors we fetch/render. Kept in sync with the
// `limit:` in the details query so the truncation heuristic matches what we asked
// for.
const ROW_ERROR_LIMIT = 1000;

export default {
  props: ['context', 'next'],
  data() {
    return {
      getImport: null,
      importErrorDetails: null,
      isImporting: false,
    };
  },
  apollo: {
    // Status/progress only — sourced from the import record, no import-svc calls.
    // The wizard UI is gated on v-if="getImport", so keeping this query free of
    // the optional, separately-resolved error fields means it can never blank
    // because of a rowErrors/rowErrorCount backend issue.
    getImport: {
      query: gql`
        query getImport($orgId: ID!, $id: ID!) {
          getImport(orgId: $orgId, id: $id) {
            id
            status
            progress
          }
        }
      `,
      variables() {
        return {
          orgId: this.$store.state.currentOrg.id,
          id: this.context.importId,
        };
      },
      skip() {
        return this.context.importId === undefined || this.context.importId === null;
      },
      pollInterval: 2500,
    },
    // Error details: rowErrors/rowErrorCount each make a separate import-svc call,
    // so they're decoupled from the status query above. This only runs once the
    // import has SETTLED into an error-bearing state — so it makes no import-svc
    // calls during the (long) in-progress phase, and any failure here affects only
    // the error list, never the status/progress UI.
    importErrorDetails: {
      query: gql`
        query getImportErrorDetails($orgId: ID!, $id: ID!) {
          importErrorDetails: getImport(orgId: $orgId, id: $id) {
            id
            validationErrors {
              index
              errors
            }
            importErrors
            rowErrorCount {
              validate
              run
            }
            # Fetch BOTH stages explicitly: rowErrors defaults to stage "validate"
            # server-side, so a bare rowErrors(...) would never return run-stage
            # errors — leaving completed-with-errors imports with an empty table.
            validateRowErrors: rowErrors(stage: "validate", limit: 1000) {
              rowIndex
              stage
              errors
            }
            runRowErrors: rowErrors(stage: "run", limit: 1000) {
              rowIndex
              stage
              errors
            }
          }
        }
      `,
      variables() {
        return {
          orgId: this.$store.state.currentOrg.id,
          id: this.context.importId,
        };
      },
      skip() {
        if (this.context.importId === undefined || this.context.importId === null) return true;
        const imp = this.getImport;
        if (!imp) return true;
        return !ERROR_DETAIL_STATES.includes(imp.status);
      },
      pollInterval: 2500,
      // 'all' so a partial error within THIS query (e.g. rowErrors resolves but
      // rowErrorCount errors) still surfaces what did resolve, rather than Apollo
      // dropping the whole error payload under the default 'none'.
      errorPolicy: 'all',
    },
  },
  computed: {
    // Per-row errors from the decoupled importErrorDetails query, combining both
    // stages (validate + run) so run-stage failures aren't dropped. Prefer the
    // normalized rowErrors feed (v1 and v2); fall back to the legacy embedded
    // validationErrors only when the feed is empty.
    errorRows() {
      const d = this.importErrorDetails;
      if (!d) return [];
      // rowIndex is a nullable Int and can be a -1 sentinel for errors with no
      // known source row; only add the header offset for a real (>= 0) index,
      // otherwise show a dash rather than "Line 1" / "Line 2" / "Line NaN".
      const lineOf = (idx) => (idx === null || idx === undefined || idx < 0 ? '—' : idx + 2);
      // v2 prefixes each error string with "Row N: " server-side; since we render
      // our own Line column, strip that prefix so the row/line number isn't shown
      // twice and v1/v2 render consistently.
      const stripRowPrefix = (e) => (typeof e === 'string' ? e.replace(/^Row\s+\S+:\s*/, '') : e);
      const feed = [...(d.validateRowErrors || []), ...(d.runRowErrors || [])];
      const fromFeed = feed
        .filter((r) => r.errors && r.errors.length)
        .map((r) => ({ line: lineOf(r.rowIndex), stage: r.stage || 'validate', errors: r.errors.map(stripRowPrefix) }));
      if (fromFeed.length) return fromFeed;
      return (d.validationErrors || [])
        .filter((v) => v.errors && v.errors.length)
        .map((v) => ({ line: lineOf(v.index), stage: 'validate', errors: v.errors.map(stripRowPrefix) }));
    },
    // Non-row-scoped errors (e.g. run-stage failures surfaced as strings).
    errorMessages() {
      return (this.importErrorDetails && this.importErrorDetails.importErrors) || [];
    },
    rowErrorCount() {
      return (this.importErrorDetails && this.importErrorDetails.rowErrorCount) || null;
    },
    hasErrors() {
      return this.errorRows.length > 0 || this.errorMessages.length > 0;
    },
    // Show the error section whenever the CURRENT status is an error state — even
    // if the detail fetch returned nothing — so a failed import is never presented
    // with no explanation (errorDetailsUnavailable renders a fallback in that case).
    // Keying off the live status also stops a stale payload from rendering after a
    // clean re-validation, since vue-apollo doesn't clear importErrorDetails on skip.
    showErrors() {
      return !!this.getImport && ERROR_DETAIL_STATES.includes(this.getImport.status);
    },
    // The details query resolved (importErrorDetails set) but produced no renderable
    // errors — e.g. the rowErrors resolver errored under errorPolicy 'all'. Used to
    // show a "couldn't load details" note instead of a bare "Errors" header.
    errorDetailsUnavailable() {
      return this.showErrors && !!this.importErrorDetails && !this.hasErrors;
    },
    // Row errors are the only capped population (importErrors messages are returned
    // in full), so the truncation note compares row counts on a like-for-like basis.
    shownRowCount() {
      return this.errorRows.length;
    },
    totalRowCount() {
      const c = this.rowErrorCount;
      return c ? (c.validate || 0) + (c.run || 0) : this.shownRowCount;
    },
    // A stage that came back with a full page almost certainly had more dropped by
    // the LIMIT — the fallback signal when rowErrorCount isn't available to compare.
    rowErrorsTruncated() {
      const d = this.importErrorDetails;
      if (!d) return false;
      return (d.validateRowErrors || []).length >= ROW_ERROR_LIMIT || (d.runRowErrors || []).length >= ROW_ERROR_LIMIT;
    },
    // Flag when the server reports more row errors than we rendered, or (with no
    // authoritative count) when a full page came back so some were likely dropped.
    moreErrorsThanShown() {
      if (this.totalRowCount > this.shownRowCount) return true;
      return !this.rowErrorCount && this.rowErrorsTruncated;
    },
  },
  watch: {
    'getImport.status'(status) {
      // Clear the decoupled error payload whenever we leave an error state so a
      // stale set can't flash if we return to an error state later (e.g.
      // validation-failed -> validating -> validation-failed with different
      // errors) — vue-apollo doesn't clear it when its skip flips back to true.
      if (status && !ERROR_DETAIL_STATES.includes(status)) {
        this.importErrorDetails = null;
      }
      // Stop the status poll once the import can't change on its own. Only the
      // status query is stopped — the details query keeps its own lifecycle so
      // error loading is never cut short.
      if (status && TERMINAL_STATES.includes(status)) {
        const queries = this.$apollo && this.$apollo.queries;
        if (queries && queries.getImport) queries.getImport.stopPolling();
      }
    },
  },
  methods: {
    async beginImport() {
      this.isValidating = true;
      // Get upload URL
      const vars = {
        orgId: this.$store.state.currentOrg.id,
        id: this.context.importId,
      };

      await this.$apollo.mutate({
        mutation: gql`
          mutation ($orgId: ID!, $id: ID!) {
            runImport(orgId: $orgId, id: $id)
          }
        `,
        // Parameters
        variables: vars,
      });

      this.next();
    },
  },
};
</script>
